import { blaetterSlots, schluesselFehler, slotAnzahl } from "@gui-creator/gemeinsam";
import { Eraser, ListPlus } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import type { Auswahl } from "../editor/auswahl.ts";
import { BlattZu } from "../huelle/Blatt.tsx";
import { Abschnitt, menuePlatzhalter, type Kontext } from "./gemeinsam.tsx";
import { ItemWahl } from "./ItemWahl.tsx";
import { ListenFelder } from "./ListenFelder.tsx";
import { SlotFelder } from "./SlotFelder.tsx";
import { TextFeld } from "./TextFeld.tsx";
import "./eigenschaften.css";

function UiFelder({ k }: { k: Kontext }) {
  const id = useId();
  const d = k.ui.daten;
  const rollen = k.katalog.farbrollen;
  const setzen = (felder: Record<string, unknown>) => k.aendern([{ art: "ui_setzen", felder } as never]);
  return (
    <div className="panel-inhalt">
      <header className="panel-kopf">
        <h2>{k.ui.name ?? k.ui.schluessel}</h2>
        <span className="mono leise">{k.ui.schluessel}</span>
        <BlattZu />
      </header>
      <Abschnitt titel="Menü">
        <TextFeld label="Titel" wert={d.titel} onWert={(t) => setzen({ titel: t })} rollen={rollen} platzhalter={menuePlatzhalter(k)} vorschau="titel" deaktiviert={!k.darf} />
        <div className="feld">
          <label htmlFor={`${id}-q`}>Menüquelle</label>
          <select id={`${id}-q`} className="eingabe" value={d.quelle ?? ""} disabled={!k.darf} onChange={(e) => setzen({ quelle: e.target.value || null })}>
            <option value="">Keine</option>
            {k.katalog.quellen.map((q) => (
              <option key={q.schluessel} value={q.schluessel}>
                {q.name}
              </option>
            ))}
          </select>
          <span className="feld-hinweis">Liefert Werte wie Münzen oder Level für Titel, Texte und feste Slots.</span>
        </div>
        <ItemWahl label="Rahmen für freie Slots" wert={d.rahmen.symbol} deaktiviert={!k.darf} onWert={(s) => setzen({ rahmen: { symbol: s } })} />
      </Abschnitt>
      <Abschnitt titel="Bedrock-Formular">
        <TextFeld
          label="Kopftext"
          mehrzeilig
          wert={d.text.join("\n")}
          onWert={(t) => setzen({ text: t === "" ? [] : t.split("\n") })}
          rollen={rollen}
          platzhalter={menuePlatzhalter(k)}
          leer="Steht über den Knöpfen"
          hinweis="Nur Bedrock zeigt diesen Text; Java hat dafür keinen Platz."
          deaktiviert={!k.darf}
        />
      </Abschnitt>
      <p className="panel-hinweis zahlen">
        {Object.keys(d.slots).length} feste Slots · {d.listen.length} {d.listen.length === 1 ? "Listenbereich" : "Listenbereiche"} · {slotAnzahl(d.form)} Plätze
      </p>
    </div>
  );
}

function MehrfachFelder({ k, slots }: { k: Kontext; slots: number[] }) {
  const id = useId();
  const [neu, setNeu] = useState(false);
  const namen = new Set(k.ui.daten.listen.map((l) => l.name));
  const vorschlag = ["liste", "liste2", "liste3", "liste4"].find((n) => !namen.has(n)) ?? `liste${namen.size + 1}`;
  const [name, setName] = useState(vorschlag);
  const [quelle, setQuelle] = useState(k.katalog.quellen[0]?.schluessel ?? "");
  const belegt = slots.filter((s) => k.ui.daten.slots[s]);
  const inListe = slots.some((s) => k.ui.daten.listen.some((l) => l.slots.includes(s)));
  const fehler = schluesselFehler(name) ?? (namen.has(name) ? "Diesen Namen hat schon ein Listenbereich." : null);

  return (
    <div className="panel-inhalt">
      <header className="panel-kopf">
        <h2 className="zahlen">{slots.length} Slots gewählt</h2>
        <BlattZu />
      </header>
      {k.darf && (
        <Abschnitt titel="Aktionen">
          {!neu ? (
            <div className="knopfreihe">
              <button type="button" className="knopf" disabled={inListe} onClick={() => setNeu(true)} title={inListe ? "Ein gewählter Slot gehört schon zu einer Liste." : undefined}>
                <ListPlus size={14} aria-hidden="true" /> Als Listenbereich festlegen
              </button>
              {belegt.length > 0 && (
                <button type="button" className="knopf" onClick={() => k.aendern([{ art: "slots_leeren", indizes: belegt }])}>
                  <Eraser size={14} aria-hidden="true" /> {belegt.length} leeren
                </button>
              )}
            </div>
          ) : (
            <form
              className="feldgruppe"
              onSubmit={(e) => {
                e.preventDefault();
                if (fehler || !quelle) return;
                k.aendern([{ art: "listenbereich_setzen", bereich: { name, quelle, slots, vorlage: { symbol: "minecraft:paper", name: "{name}" } } }]);
                setNeu(false);
              }}
            >
              <div className="feld">
                <label htmlFor={`${id}-n`}>Name des Bereichs</label>
                <input id={`${id}-n`} className="eingabe mono" value={name} onChange={(e) => setName(e.target.value)} aria-invalid={!!fehler} autoFocus />
                {fehler && <span className="feld-fehler">{fehler}</span>}
              </div>
              <div className="feld">
                <label htmlFor={`${id}-q`}>Datenquelle</label>
                <select id={`${id}-q`} className="eingabe" value={quelle} onChange={(e) => setQuelle(e.target.value)}>
                  {k.katalog.quellen.length === 0 && <option value="">Der Katalog hat noch keine Quellen</option>}
                  {k.katalog.quellen.map((q) => (
                    <option key={q.schluessel} value={q.schluessel}>
                      {q.name}
                    </option>
                  ))}
                </select>
              </div>
              {belegt.length > 0 && <p className="feld-hinweis">Die {belegt.length} belegten Slots werden dabei geleert.</p>}
              <div className="knopfreihe">
                <button type="button" className="knopf" onClick={() => setNeu(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="knopf knopf-primaer" disabled={!!fehler || !quelle}>
                  Festlegen
                </button>
              </div>
            </form>
          )}
        </Abschnitt>
      )}
    </div>
  );
}

/** Zeigt je nach Auswahl die Felder der UI, eines Slots, eines Listenbereichs oder mehrerer Slots. */
export function Eigenschaften({ k, auswahl, oben, unten }: { k: Kontext; auswahl: Auswahl; oben?: ReactNode; unten?: ReactNode }) {
  const d = k.ui.daten;
  let inhalt: ReactNode;
  const bereichNamentlich = auswahl.bereich ? d.listen.find((l) => l.name === auswahl.bereich) : undefined;
  if (bereichNamentlich) {
    inhalt = <ListenFelder key={`l-${bereichNamentlich.name}`} k={k} bereich={bereichNamentlich} />;
  } else if (auswahl.slots.length === 1) {
    const i = auswahl.slots[0]!;
    const liste = d.listen.find((l) => l.slots.includes(i));
    const blaetter = d.listen.find((l) => blaetterSlots({ ...d, listen: [l] }).includes(i));
    const art = blaetter ? (["zurueck", "weiter", "info"] as const).find((a) => blaetter.blaettern?.[a]?.slot === i) : undefined;
    if (liste) inhalt = <ListenFelder key={`l-${liste.name}`} k={k} bereich={liste} />;
    else if (blaetter && art) inhalt = <ListenFelder key={`b-${blaetter.name}-${art}`} k={k} bereich={blaetter} blaetterSlot={art} />;
    else inhalt = <SlotFelder key={`s-${i}`} k={k} index={i} />;
  } else if (auswahl.slots.length > 1) {
    inhalt = <MehrfachFelder key={auswahl.slots.join(",")} k={k} slots={auswahl.slots} />;
  } else {
    inhalt = <UiFelder k={k} />;
  }
  return (
    <aside className="eigenschaften" aria-label="Eigenschaften">
      {oben}
      {inhalt}
      {unten}
    </aside>
  );
}
